import { RouteModule } from '#/horizon/components/RouteModule';
import { getServerRouterContext } from '#/horizon/router-contexts/server-router-context';
import { useI18n } from '#/i18n/hooks/useI18n';
import { I18nLocale } from '#/i18n/types/I18nLocale';
import type {
  LayoutHandleElements,
  LayoutHandleProps,
} from '#/layouts/Layout/LayoutHandleProps';
import { DynamicSearchInternshipRefinements } from '#/modules/search/internships/components/Refinements/DynamicSearchInternshipRefinements';
import { searchInternshipsRoutePathByLocale } from '#/modules/search/internships/routing/searchInternships.route-paths';
import { searchInternshipsRouteReference } from '#/modules/search/routing/search.routes';
import { getSearchPageLinkRels } from '#/modules/search/utils/getSearchPageLinkRels';
import type { HorizonAppRouteLoaderArgs } from '#/routing/classes/types/AppRouteLoaderArgs';
import type { HorizonAppRouteComponentProps } from '#/routing/types/HorizonAppRouteComponentProps';
import { getStaticImageSrc } from '#/utils/getImageSrc';

import { SearchInternshipsPage } from './SearchInternshipsPage';

export const handle = {
  searchRefinements: <DynamicSearchInternshipRefinements />,
} satisfies LayoutHandleElements;

export function loader({
  url,
  context,
}: HorizonAppRouteLoaderArgs<typeof searchInternshipsRouteReference>) {
  const { serverContext } = getServerRouterContext(context);

  return {
    handle: {
      urlByLocale: searchInternshipsRoutePathByLocale,
      darkBackground: true,
      defaultSearchType: 'INTERNSHIP',
      hideFooterDivider: true,
      searchFromPageType: 'SEARCH',
      showDesktopSearch: true,
      isSearchPage: true,
      showSearchRefinements: true,
    } satisfies LayoutHandleProps,

    linkRels: getSearchPageLinkRels({
      searchParams: url.searchParams,
      locale: serverContext.locale,
      routePathByLocale: searchInternshipsRoutePathByLocale,
      referenceRoute: searchInternshipsRouteReference,
    }),

    now: serverContext.now,
  };
}

export default function SearchInternshipsHorizonRoute({
  loaderData: { linkRels, now },
}: HorizonAppRouteComponentProps<typeof loader>) {
  const { i18n, locale } = useI18n();

  const ogImage = (() => {
    switch (locale) {
      case I18nLocale.EN:
        return getStaticImageSrc('og-image-en-internships.jpg');
      case I18nLocale.ES:
        return;
      case I18nLocale.PT:
        return getStaticImageSrc('og-image-pt-internships.jpg');
    }
  })();

  return (
    <RouteModule
      title={
        /* TRANSLATORS: internship search page title */
        i18n('Best Internships Near You, Remote Internships')
      }
      description={
        /* TRANSLATORS: internship search page description */
        i18n('Internships with leading nonprofits and social-impact companies')
      }
      linkRels={linkRels}
      ogImage={ogImage}
    >
      <SearchInternshipsPage now={now} />
    </RouteModule>
  );
}
